import {gql} from '@apollo/client';
import {useEffect} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from 'instruments/equipment/EquipmentConstants';
import {AllTagSchemasForProceduresQuery} from 'procedures/utils/__generated/AllTagSchemasForProceduresQuery';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import {SchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';

import {TagSchemaListingWithFieldsGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';

// Put any new TagSchema information (that isn't included in ALL_TAG_SCHEMAS_QUERY) here
export const ALL_TAG_SCHEMAS_FOR_PROCEDURES = gql`
  query AllTagSchemasForProceduresQuery @skipAudit @engComponent(name: PROCEDURES) {
    tagSchemas {
      id
      interfaces {
        id
        name
      }
    }
  }
`;

export interface TagSchemaForProcedures {
  id: string;
  interfaces: Array<SchemaInterfaceSummary>;
}

export function useAllTagSchemasWithProceduresShape(): Array<TagSchemaForProcedures> | null {
  const tagSchemasResult = useSkippableBenchlingQuery<AllTagSchemasForProceduresQuery>(
    ALL_TAG_SCHEMAS_FOR_PROCEDURES,
    {
      renderCachedDataWhenRefetching: true,
    }
  );

  return tagSchemasResult.type === QueryResultType.SUCCESS ? tagSchemasResult.data.tagSchemas : null;
}

export function useAllTagSchemasWithStandardShape(
  onLoaded?: (tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>) => void
): Array<TagSchemaListingWithFieldsGraphQL> | null {
  // Do NOT change the graphql being queried here! This is an expensive query so we want to rely on Apollo
  // caching. 99% of the time the user already navigated through the benchling settings page, and that page
  // already did the ALL_TAG_SCHEMAS_QUERY so we get it for free. If you want new information put that in
  // useAllTagSchemasWithProceduresShape()
  const tagSchemasResult = useSkippableBenchlingQuery<AllTagSchemasQuery>(ALL_TAG_SCHEMAS_QUERY, {
    renderCachedDataWhenRefetching: true,
  });

  const data = tagSchemasResult.type === QueryResultType.SUCCESS ? tagSchemasResult.data.tagSchemas : null;

  useEffect(() => {
    if (tagSchemasResult.type === QueryResultType.SUCCESS) {
      const ts = tagSchemasResult.data.tagSchemas;
      onLoaded?.(ts);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [tagSchemasResult.type]);

  return data;
}

export function doesSchemaImplementEquipmentInterface(schema: TagSchemaForProcedures): boolean {
  return schema.interfaces.some((ssi) => ssi.id === EQUIPMENT_SCHEMA_INTERFACE_ID);
}
